import {
  ArrowBack,
  ChecklistOutlined,
  Close,
  DashboardOutlined,
  DescriptionOutlined,
  EventOutlined,
  HandshakeOutlined,
  HelpOutlineOutlined,
  KeyboardArrowDown,
  Logout,
  MailOutline,
  Menu as MenuIcon,
  MenuBookOutlined,
  MoreHoriz,
  PaymentsOutlined,
  PeopleAltOutlined,
  RequestQuoteOutlined,
  SchoolOutlined,
  SettingsOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { Drawer, IconButton, Popover } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { signOut } from "utils/signOut";
import { PushNotificationsProvider } from "pushNotifications/PushNotificationsProvider";
import { ADMIN_HOME, currentUserIsStaffInPartnerPortal } from "utils/referralRouting";
import {
  PARTNER_NAV_SECTIONS,
  findActiveNavItem,
  getMobileMoreNav,
  getMobilePrimaryNav,
  getSidebarSections,
} from "../navigation";
import { partnerQueryKeys } from "../api/queryKeys";
import { fetchPartnerMe } from "../api/endpoints/me";
import { fetchPartnerUnreadMessageCount } from "../api/endpoints/messages";
import { fetchPartnerUnreadNotificationCount } from "../api/endpoints/notifications";
import { fetchSupportUnreadCount } from "../api/endpoints/support";
import NotificationBell from "../components/NotificationBell";
import AssistantLauncher from "components/Assistant/AssistantLauncher";
import usePartnerQuery from "../hooks/usePartnerQuery";

const NAV_ICONS = {
  dashboard: DashboardOutlined,
  clients: PeopleAltOutlined,
  documents: DescriptionOutlined,
  tasks: TaskAltOutlined,
  appointments: EventOutlined,
  payments: PaymentsOutlined,
  "price-list": RequestQuoteOutlined,
  "check-list": ChecklistOutlined,
  courses: MenuBookOutlined,
  training: SchoolOutlined,
  help: HelpOutlineOutlined,
  settings: SettingsOutlined,
};

const initialsOf = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "RP";

const CountBadge = ({ count }) =>
  count > 0 ? (
    <span className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const NavItem = ({ item, badge, onNavigate }) => {
  const Icon = NAV_ICONS[item.id];
  const { pathname } = useLocation();
  const aliasActive = (item.aliases || []).some((path) => pathname.startsWith(path));
  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      className={({ isActive: pathActive }) => {
        const isActive = pathActive || aliasActive;
        return [
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium no-underline transition-colors min-h-[40px]",
          isActive
            ? "bg-white/10 text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-teal-400"
            : "text-slate-300 hover:bg-white/5 hover:text-white",
        ].join(" ");
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 19 }} className="opacity-80 group-hover:opacity-100" /> : null}
      <span className="flex-1 truncate">{item.label}</span>
      <CountBadge count={badge} />
    </NavLink>
  );
};

const SidebarBody = ({ badges, onNavigate, onLeavePortal }) => {
  const sections = getSidebarSections();
  return (
    <>
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10 shrink-0">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500/20 text-teal-300">
          <HandshakeOutlined fontSize="small" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold m-0 leading-tight">Referral portal</p>
          <p className="text-[11px] text-slate-400 m-0 leading-tight">Kothar partner workspace</p>
        </div>
      </div>

      {onLeavePortal ? (
        <button
          type="button"
          onClick={onLeavePortal}
          className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-white/15 bg-transparent px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-white/5 hover:text-white min-h-[40px] cursor-pointer"
        >
          <ArrowBack sx={{ fontSize: 16 }} />
          <span>Organization dashboard</span>
        </button>
      ) : null}

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Partner modules">
        {sections.map((section) => (
          <div key={section.id} className="mb-4 last:mb-0">
            {section.label ? (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 m-0">
                {section.label}
              </p>
            ) : null}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  badge={badges[item.badgeKey] || 0}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </>
  );
};

/** Avatar + name in the header; opens the partner's account details and sign-out. */
const AccountMenu = ({ partner, onNavigate, onSignOut }) => {
  const [anchor, setAnchor] = useState(null);
  const name = partner?.displayName || "Referral partner";
  const close = () => setAnchor(null);
  const go = (path) => {
    close();
    onNavigate(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-2 py-1 shadow-sm hover:border-slate-300 cursor-pointer min-h-[40px]"
        aria-label="Account menu"
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-teal-700 text-xs font-bold text-white">
          {initialsOf(partner?.displayName)}
        </span>
        <span className="hidden md:block text-left max-w-[160px]">
          <span className="block text-xs font-semibold text-slate-800 truncate">{name}</span>
          {partner?.tradingName ? (
            <span className="block text-[11px] text-slate-500 truncate">{partner.tradingName}</span>
          ) : null}
        </span>
        <KeyboardArrowDown sx={{ fontSize: 18 }} className="hidden md:block text-slate-400" />
      </button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 260, borderRadius: "14px", mt: 1 } }}
      >
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900 m-0 truncate">{name}</p>
          {partner?.tradingName ? (
            <p className="text-xs text-slate-500 m-0 mt-0.5 truncate">{partner.tradingName}</p>
          ) : null}
          {partner?.email ? (
            <p className="text-xs text-slate-400 m-0 mt-0.5 truncate">{partner.email}</p>
          ) : null}
        </div>
        <div className="py-1">
          {[
            { label: "Settings", path: "/partner/settings", Icon: SettingsOutlined },
            { label: "Help & Support", path: "/partner/help", Icon: HelpOutlineOutlined },
          ].map(({ label, path, Icon }) => (
            <button
              key={path}
              type="button"
              onClick={() => go(path)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 bg-transparent border-0 hover:bg-slate-50 cursor-pointer text-left"
            >
              <Icon sx={{ fontSize: 18 }} className="text-slate-400" />
              {label}
            </button>
          ))}
        </div>
        <div className="border-t border-slate-100 py-1">
          <button
            type="button"
            onClick={() => {
              close();
              onSignOut();
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-rose-600 bg-transparent border-0 hover:bg-rose-50 cursor-pointer text-left"
          >
            <Logout sx={{ fontSize: 18 }} />
            Sign out
          </button>
        </div>
      </Popover>
    </>
  );
};

/**
 * Partner workspace shell — intentionally unlike the org admin CRM chrome.
 * Mobile: top bar + bottom primary tabs + full menu drawer.
 * Desktop: persistent left sidebar.
 */
const PortalShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  // A colleague who refers on the side lives in both apps; an outside partner has only this one.
  const staff = currentUserIsStaffInPartnerPortal();
  const leavePortal = () => navigate(ADMIN_HOME);
  const { data: partner } = usePartnerQuery(partnerQueryKeys.me(), fetchPartnerMe, {
    staleTime: 5 * 60 * 1000,
  });
  // Badge counts stay fresh for a minute instead of refetching on every window focus:
  // reading a message, notification or ticket invalidates its own key explicitly.
  const badgeOptions = { staleTime: 60_000, refetchOnWindowFocus: false };
  // The badge is the server's unread count, so it agrees with what the Alerts page marks read.
  const { data: unreadMessages } = usePartnerQuery(
    partnerQueryKeys.messages({ view: "unread-count" }),
    fetchPartnerUnreadMessageCount,
    badgeOptions
  );
  // Alerts are the notification rows the office raised; the inbox badge stays the message count.
  const { data: unreadNotifications } = usePartnerQuery(
    partnerQueryKeys.notificationCount(),
    fetchPartnerUnreadNotificationCount,
    badgeOptions
  );
  // Tickets the office has answered or moved since the partner last opened them.
  const { data: unreadTickets } = usePartnerQuery(
    partnerQueryKeys.supportUnread(),
    fetchSupportUnreadCount,
    badgeOptions
  );
  const badges = {
    unreadAlerts: Number(unreadMessages) || 0,
    unreadNotifications: Number(unreadNotifications) || 0,
    unreadTickets: Number(unreadTickets) || 0,
  };

  const primaryNav = useMemo(() => getMobilePrimaryNav(), []);
  const moreNav = useMemo(() => getMobileMoreNav(), []);
  const moreActive = moreNav.some((item) => location.pathname.startsWith(item.path));
  const activeItem = findActiveNavItem(location.pathname);
  const activeSection =
    activeItem && activeItem.section !== "overview" && activeItem.section !== "topbar"
      ? PARTNER_NAV_SECTIONS.find((section) => section.id === activeItem.section)?.label
      : null;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-[#eef3f1] text-slate-900 lg:grid lg:grid-cols-[248px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex bg-[#0b2428] text-white flex-col min-h-screen sticky top-0 h-screen">
        <SidebarBody badges={badges} onLeavePortal={staff ? leavePortal : undefined} />
      </aside>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={closeMenu}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: "min(86vw, 300px)",
            backgroundColor: "#0b2428",
            color: "#fff",
          },
        }}
      >
        <div className="relative flex flex-col h-full min-h-0">
          <IconButton
            onClick={closeMenu}
            aria-label="Close menu"
            sx={{ color: "white", position: "absolute", top: 12, right: 8, zIndex: 1 }}
          >
            <Close />
          </IconButton>
          <SidebarBody
            badges={badges}
            onNavigate={closeMenu}
            onLeavePortal={
              staff
                ? () => {
                    closeMenu();
                    leavePortal();
                  }
                : undefined
            }
          />
        </div>
      </Drawer>

      <div className="min-w-0 min-h-screen flex flex-col pb-[calc(4.25rem+env(safe-area-inset-bottom))] lg:pb-0">
        <header className="sticky top-0 z-20 h-16 border-b border-slate-200/80 bg-white/90 backdrop-blur px-3 sm:px-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <IconButton
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              sx={{ display: { lg: "none" }, color: "#0f172a" }}
            >
              <MenuIcon />
            </IconButton>
            <div className="min-w-0">
              {activeSection ? (
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-700 m-0 leading-tight">
                  {activeSection}
                </p>
              ) : null}
              <p className="text-base font-bold text-slate-900 m-0 truncate leading-tight">
                {activeItem?.label || "Referral portal"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {staff ? (
              <button
                type="button"
                onClick={leavePortal}
                className="hidden lg:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm min-h-[40px] cursor-pointer"
                title="Back to the organization dashboard"
              >
                <ArrowBack sx={{ fontSize: 16 }} />
                Org dashboard
              </button>
            ) : null}
            <NotificationBell unreadCount={badges.unreadNotifications} />
            <IconButton
              onClick={() => navigate("/partner/alerts")}
              aria-label={
                badges.unreadAlerts ? `Messages, ${badges.unreadAlerts} unread` : "Messages"
              }
              sx={{ color: "#0f4c4c" }}
            >
              <span className="relative inline-flex">
                <MailOutline />
                {badges.unreadAlerts > 0 ? (
                  <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
                    {badges.unreadAlerts > 99 ? "99+" : badges.unreadAlerts}
                  </span>
                ) : null}
              </span>
            </IconButton>
            <span className="hidden sm:block h-6 w-px bg-slate-200 mx-1" aria-hidden="true" />
            <AccountMenu
              partner={partner}
              onNavigate={navigate}
              onSignOut={() => signOut({ queryClient, navigate })}
            />
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-5 sm:py-7 w-full max-w-full overflow-x-hidden">
          <Outlet />
        </main>

        <AssistantLauncher
          surface="partner"
          className="right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:right-6 lg:bottom-6"
        />

        {/* Mobile bottom primary nav */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          aria-label="Primary partner modules"
        >
          <div className="grid grid-cols-5 gap-0 px-1 pt-1 pb-1">
            {primaryNav.map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  [
                    "relative flex flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-2 text-[10px] font-semibold min-h-[52px] no-underline",
                    isActive ? "text-teal-800 bg-teal-50" : "text-slate-500",
                  ].join(" ")
                }
              >
                <span className="truncate max-w-full">{item.shortLabel || item.label}</span>
                {item.badgeKey && badges[item.badgeKey] > 0 ? (
                  <span className="absolute top-1 right-2 h-1.5 w-1.5 rounded-full bg-rose-500" />
                ) : null}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className={[
                "flex flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-2 text-[10px] font-semibold min-h-[52px]",
                moreActive ? "text-teal-800 bg-teal-50" : "text-slate-500",
              ].join(" ")}
              aria-label="More modules"
            >
              <MoreHoriz fontSize="small" />
              <span>More</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
};

const ReferralPortalLayout = () => (
  <PushNotificationsProvider>
    <PortalShell />
  </PushNotificationsProvider>
);

export default ReferralPortalLayout;
